/** Node labels, display styles and thumbnails from `describe-nodes` (spec §15.6). Pure. */
import type { MappedGraph, NodeKind } from "./conventionMapper";

export interface NodeDescription {
  iri: string;
  label: string;
  kind: string;
  types: string[];
  displayStyle?: string | null;
  roleStyle?: string | null;
  blockType?: string | null;
  thumbnailUrl?: string | null;
}

export interface NodeInfo {
  label: string;
  style: string;
  kind: string;
  thumb: string | null;
  types: string[];
}

export function localName(iri: string): string {
  const cut = Math.max(iri.lastIndexOf("#"), iri.lastIndexOf("/"), iri.lastIndexOf(":"));
  const tail = cut >= 0 ? iri.slice(cut + 1) : iri;
  return tail || iri;
}

const KIND_STYLE: Partial<Record<NodeKind | string, string>> = {
  doc: "document",
  concept: "concept",
  annotation: "annotation",
  sequence: "sequence",
  block: "block",
};

export function buildNodeInfo(graph: MappedGraph | null, descriptions: NodeDescription[]): Map<string, NodeInfo> {
  const byIri = new Map(descriptions.map((d) => [d.iri, d]));
  const out = new Map<string, NodeInfo>();
  for (const n of graph?.nodes ?? []) {
    const d = byIri.get(n.id);
    const kind = d?.kind ?? n.kind;
    // a planning role (km:role) is what the author says the block *is*, so it wins; otherwise block
    // nodes use `block-<type>`, which takes precedence over ontology styles (spec §15.6 step 3)
    const style = d?.roleStyle ?? (d?.blockType ? `block-${d.blockType}` : (d?.displayStyle ?? KIND_STYLE[kind] ?? "default"));
    out.set(n.id, {
      label: d?.label || n.label || localName(n.id),
      style,
      kind,
      thumb: d?.thumbnailUrl ?? null,
      types: d?.types?.length ? d.types : n.types,
    });
  }
  return out;
}
